import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenant, normalizeBranding } from '../context/TenantContext.jsx';
import { authAPI } from '../services/api.js';
import {
    clearLastTenantSlug,
    getLastTenantSlug,
    persistLastTenantSlug,
    resolvePostLoginPath,
    saveUserLoginSlug,
} from '../utils/sessionNavigation.js';
import './Login.css';

const ROLE_LABELS = {
    worker: 'operario',
    supervisor: 'supervisor',
    admin: 'administrador',
    superadmin: 'súper administrador',
};

/**
 * Paso 1: código → verificación API.
 * Paso 2: contraseña (solo si el rol la requiere) → login.
 * Operario: un solo paso sin contraseña.
 */
export default function Login() {
    const { login, platform } = useAuth();
    const [seedDataVisible, setSeedDataVisible] = useState(platform?.seedDataVisible ?? false);
    const { setBranding } = useTenant();
    const setBrandingRef = useRef(setBranding);
    setBrandingRef.current = setBranding;

    const navigate = useNavigate();
    const { tenantSlug: tenantSlugParam } = useParams();
    const [searchParams] = useSearchParams();

    const lockedSlug = (
        tenantSlugParam ||
        searchParams.get('empresa') ||
        searchParams.get('tenant') ||
        ''
    )
        .trim()
        .toLowerCase();

    const isCompanyLogin = Boolean(lockedSlug);
    const slugForApi = isCompanyLogin ? lockedSlug : undefined;

    const [companyName, setCompanyName] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);
    const [tenantLoading, setTenantLoading] = useState(isCompanyLogin);
    const [tenantWarn, setTenantWarn] = useState('');

    /** 'code' = solo código visible; 'password' = pedir contraseña */
    const [step, setStep] = useState('code');
    const [verifiedRole, setVerifiedRole] = useState('');
    const passwordRef = useRef(null);

    useEffect(() => {
        localStorage.removeItem('alinea_token');
        localStorage.removeItem('alinea_user');
        localStorage.removeItem('alinea_platform');
        localStorage.removeItem('alinea_superadmin_tenant_id');
    }, []);

    useEffect(() => {
        if (!isCompanyLogin) {
            setTenantLoading(false);
            setCompanyName('');
            return;
        }

        let cancelled = false;
        setTenantLoading(true);
        fetch(`/api/platform/tenant/${encodeURIComponent(lockedSlug)}`)
            .then(async (r) => {
                const body = await r.json().catch(() => ({}));
                if (cancelled) return;
                if (r.ok && body?.success) {
                    persistLastTenantSlug(lockedSlug);
                    setCompanyName(body.data.name);
                    if (body.data.branding) {
                        setBrandingRef.current(normalizeBranding(body.data.branding));
                    }
                } else if (r.status === 404) {
                    if (getLastTenantSlug() === lockedSlug) clearLastTenantSlug();
                    setError(
                        body?.error ||
                            'Esta empresa ya no existe. Use el enlace de su cliente (por ejemplo /login/c-es).'
                    );
                } else {
                    setCompanyName(`Empresa (${lockedSlug})`);
                    setTenantWarn('Nombre de empresa no disponible; puede intentar entrar.');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setCompanyName(`Empresa (${lockedSlug})`);
                    setTenantWarn('Sin conexión con la API. Compruebe que el servidor esté en marcha.');
                }
            })
            .finally(() => {
                if (!cancelled) setTenantLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [lockedSlug, isCompanyLogin]);

    useEffect(() => {
        if (platform?.seedDataVisible != null) {
            setSeedDataVisible(platform.seedDataVisible);
            return;
        }
        fetch('/api/platform/features')
            .then((r) => r.json())
            .then((body) => setSeedDataVisible(!!body?.data?.seedDataVisible))
            .catch(() => setSeedDataVisible(false));
    }, [platform?.seedDataVisible]);

    function resetToCodeStep() {
        setStep('code');
        setVerifiedRole('');
        setPassword('');
        setInfo('');
    }

    async function checkCodeOnServer(normalizedCode) {
        const res = await authAPI.checkCode(normalizedCode, slugForApi);
        const body = res.data ?? {};
        if (res.status >= 400) {
            throw new Error(body.error || 'No se pudo verificar el código');
        }
        if (body.code === 'TENANT_LOGIN_REQUIRED') {
            throw new Error(body.error || 'Use el enlace de su empresa (/login/…).');
        }
        if (!body?.found) {
            throw new Error(body?.error || 'Código no encontrado');
        }
        return body;
    }

    async function performLogin(normalizedCode, pass) {
        setLoading(true);
        setError('');
        try {
            const res = await login(normalizedCode, pass, slugForApi);
            if (res.success) {
                if (res.branding) setBranding(normalizeBranding(res.branding));
                const sessionUser = res.user;
                if (lockedSlug) {
                    saveUserLoginSlug(sessionUser, lockedSlug);
                    persistLastTenantSlug(lockedSlug);
                } else if (sessionUser?.tenantSlug || sessionUser?.tenant_slug) {
                    const slug = sessionUser.tenantSlug || sessionUser.tenant_slug;
                    saveUserLoginSlug(sessionUser, slug);
                    persistLastTenantSlug(slug);
                }
                navigate(resolvePostLoginPath(sessionUser), { replace: true });
                return;
            }
            if (res.passwordRequired) {
                setStep('password');
                setInfo('Introduce tu contraseña de acceso.');
                passwordRef.current?.focus();
                return;
            }
            setError(res.message || 'No se pudo iniciar sesión');
        } finally {
            setLoading(false);
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const normalizedCode = code.trim().toUpperCase();
        if (!normalizedCode) return;

        if (step === 'password') {
            if (!password.trim()) {
                setError('Escribe tu contraseña.');
                passwordRef.current?.focus();
                return;
            }
            await performLogin(normalizedCode, password.trim());
            return;
        }

        setLoading(true);
        setError('');
        setInfo('');
        try {
            const body = await checkCodeOnServer(normalizedCode);
            const roleLabel = ROLE_LABELS[body.role] || body.role;
            setVerifiedRole(body.role);

            if (body.requiresPassword) {
                setStep('password');
                setInfo(`Código válido (${roleLabel}). Introduce tu contraseña y pulse Confirmar acceso.`);
                passwordRef.current?.focus();
            } else {
                setInfo(`Código válido (${roleLabel}). Entrando…`);
                await performLogin(normalizedCode, null);
            }
        } catch (err) {
            const msg =
                err.response?.data?.error ||
                err.message ||
                'No se pudo verificar el código. Compruebe el enlace de su empresa.';
            setError(msg);
            resetToCodeStep();
        } finally {
            setLoading(false);
        }
    }

    const showPasswordField = step === 'password';

    return (
        <div className="login-page">
            <div className="login-bg-orb login-bg-orb-1" />
            <div className="login-bg-orb login-bg-orb-2" />

            <div className="login-container login-container-stable">
                <div className="login-brand">
                    <div className="login-logo" style={{ color: 'var(--color-primary)' }}>⚖️</div>
                    <h1 className="login-title" style={{ color: 'var(--color-primary)' }}>ALINEA</h1>
                    <p className="login-subtitle">Labra tu maestría</p>
                </div>

                {isCompanyLogin && (
                    <div className="login-company-banner" aria-live="polite">
                        {tenantLoading ? (
                            <span className="text-sm text-muted">Cargando empresa…</span>
                        ) : companyName ? (
                            <>
                                <span className="login-company-label">Acceso exclusivo</span>
                                <strong className="login-company-name">{companyName}</strong>
                            </>
                        ) : null}
                        {tenantWarn && !tenantLoading && (
                            <p className="login-company-warn text-xs">{tenantWarn}</p>
                        )}
                    </div>
                )}

                {!isCompanyLogin && (
                    <p className="login-platform-note text-sm text-muted text-center">
                        Acceso de plataforma. También puede usar el enlace de cada cliente (/login/qfsas, /login/c-es).
                    </p>
                )}

                <form className="login-form" onSubmit={handleSubmit}>
                    <div className="login-input-group">
                        <label htmlFor="employee-code" className="login-label">
                            Código de empleado
                        </label>
                        <input
                            id="employee-code"
                            type="text"
                            className="input login-input"
                            placeholder={isCompanyLogin ? 'Ej: OP202609' : 'Ej: ADM001'}
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value.toUpperCase());
                                if (step === 'password') resetToCodeStep();
                                if (error) setError('');
                            }}
                            autoComplete="username"
                            autoFocus={!showPasswordField}
                            readOnly={showPasswordField}
                        />
                        {showPasswordField && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm login-change-code"
                                onClick={resetToCodeStep}
                            >
                                Cambiar código
                            </button>
                        )}
                    </div>

                    {showPasswordField && (
                        <div className="login-input-group login-password-step">
                            <label htmlFor="password" className="login-label">
                                Contraseña de acceso
                            </label>
                            <input
                                id="password"
                                ref={passwordRef}
                                type="password"
                                className="input login-input"
                                placeholder="Contraseña"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (error) setError('');
                                }}
                                autoComplete="current-password"
                                autoFocus
                            />
                        </div>
                    )}

                    {info && !error && <p className="login-code-ok text-xs">{info}</p>}
                    {error && <div className="login-error animate-fade">⚠️ {error}</div>}

                    <button
                        type="submit"
                        className="btn btn-primary btn-lg w-full"
                        disabled={loading || !code.trim() || (isCompanyLogin && tenantLoading)}
                    >
                        {loading ? (
                            <span className="spinner" />
                        ) : showPasswordField ? (
                            'Confirmar acceso'
                        ) : (
                            'Continuar'
                        )}
                    </button>

                    {seedDataVisible && step === 'code' && (
                        <div className="demo-accounts mt-lg pt-md border-top">
                            <p className="text-xs text-muted mb-sm text-center">Solo entorno de pruebas</p>
                            {!isCompanyLogin && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary w-full mb-sm"
                                    disabled={loading}
                                    onClick={() => {
                                        setCode('ADM001');
                                        setPassword('Alinea2026!');
                                        setStep('password');
                                        setInfo('Demo: pulse Confirmar acceso.');
                                    }}
                                >
                                    Demo súper admin
                                </button>
                            )}
                            {lockedSlug === 'c-es' && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary w-full mb-sm"
                                        disabled={loading}
                                        onClick={() => {
                                            setCode('OP202609');
                                            setPassword('valentina2026');
                                            setStep('password');
                                            setVerifiedRole('supervisor');
                                            setInfo('Demo supervisor C&E: pulse Confirmar acceso.');
                                        }}
                                    >
                                        Demo supervisor C&E
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary w-full"
                                        disabled={loading}
                                        onClick={() => {
                                            setCode('OP202610');
                                            setPassword('cesar2024');
                                            setStep('password');
                                            setVerifiedRole('admin');
                                            setInfo('Demo admin C&E: pulse Confirmar acceso.');
                                        }}
                                    >
                                        Demo admin C&E
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </form>

                <p className="login-hint text-muted text-xs text-center mt-lg">
                    Al ingresar, aceptas nuestros <Link to="/terms">Términos</Link> y{' '}
                    <Link to="/privacy">Privacidad</Link>.
                </p>
            </div>
        </div>
    );
}
